import { BMT_BLOWSCAN_TEMPLATE } from './defaultTemplate';
import { recipeJsonToFieldValues } from './recipeImport';
import sampleBmtRecipe from './sampleBmtRecipe.json';

/**
 * localStorage-backed persistence for recipes only - still a local POC (see
 * open question #3 "Recipes file availability across sites" in
 * `my-docs/docs/IndustrialSetup/HmiServerDrivenUi`). HMI template
 * persistence itself now goes through the real HMI Configuration service
 * (see `src/hmi/api.ts`), not this module.
 */

// Deliberately NOT keyed by site - recipes must be readable from any site,
// see open question #3 "Recipes file availability across sites".
const KEY_RECIPES = 'pepsense.hmi.recipes.v1';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): void {
  window.localStorage.setItem(key, JSON.stringify(value));
}

export type RecipeDocument = {
  name: string;
  values: Record<string, unknown>;
  savedAt: string;
};

/**
 * First-run demo content: one recipe built from the BMT-exported
 * `sampleBmtRecipe.json` sample (parameter names matched by `testInfoKey`),
 * so the Process screen's "Load Process" file picker has something to show
 * before any operator has saved one.
 */
function ensureRecipesSeeded(): void {
  // Checks raw presence (not just an empty array) so deleting the last saved
  // recipe doesn't cause it to reappear on the next read.
  const hasBeenInitialized = window.localStorage.getItem(KEY_RECIPES) !== null;
  if (!hasBeenInitialized) {
    const values = recipeJsonToFieldValues(
      BMT_BLOWSCAN_TEMPLATE,
      sampleBmtRecipe as Record<string, Record<string, unknown>>
    );
    const seed: RecipeDocument = {
      name: 'BMT Sample Recipe',
      values,
      savedAt: new Date().toISOString(),
    };
    writeJson(KEY_RECIPES, [seed]);
  }
}

export async function listRecipes(): Promise<RecipeDocument[]> {
  ensureRecipesSeeded();
  return readJson<RecipeDocument[]>(KEY_RECIPES, []);
}

export async function saveRecipe(
  name: string,
  values: Record<string, unknown>
): Promise<RecipeDocument> {
  const all = await listRecipes();
  const doc: RecipeDocument = { name, values, savedAt: new Date().toISOString() };
  const next = [...all.filter((r) => r.name !== name), doc];
  writeJson(KEY_RECIPES, next);
  return doc;
}

export async function deleteRecipe(name: string): Promise<void> {
  const all = await listRecipes();
  writeJson(
    KEY_RECIPES,
    all.filter((r) => r.name !== name)
  );
}
